/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AppShell from "./components/layout/AppShell";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import BusinessDetailsPage from "./pages/BusinessDetailsPage";
import IntentsPage from "./pages/IntentsPage";
import PdfDocumentsPage from "./pages/PdfDocumentsPage";
import SettingsPage from "./pages/SettingsPage";
import AgentToneControlPage from "./pages/AgentToneControlPage";
import {
  appMeta,
  intentsData,
  navigation,
  settingsData,
} from "./data/flowwiseData";
import NotFoundPage from "./pages/NotFoundPage";
import {
  getCurrentUser,
  isAuthenticated as hasStoredAuthToken,
} from "./utils/authHelpers";
import { logoutUser } from "./api/user-authentication";

const toastLimit = 4;

function createToastId() {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function ProtectedRoute({ isAuthenticated, children }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  const [isAuthenticated, setAuthenticated] = useState(() =>
    hasStoredAuthToken(),
  );
  const [loggedInUser, setLoggedInUser] = useState(() => getCurrentUser());
  const [currentPage, setCurrentPage] = useState("/");
  const [intentsState, setIntentsState] = useState(intentsData);
  const [settingsState, setSettingsState] = useState(settingsData);

  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    // console.log("Current URL:", window.location.pathname);
  }, [window.location]);

  const notify = useCallback(
    ({ tone = "info", title, description, duration, action }) => {
      const id = createToastId();
      const resolvedDuration = 2000;
      const nextToast = {
        id,
        tone,
        title,
        description,
        action,
        duration: resolvedDuration,
      };

      setToasts((prev) => [nextToast, ...prev].slice(0, toastLimit));
      window.setTimeout(() => dismissToast(id), resolvedDuration);
    },
    [dismissToast],
  );

  const handleLogin = useCallback((user) => {
    setAuthenticated(true);
    setLoggedInUser(user);
    // console.debug("User logged in:", user);
    console.debug("Organization ID:", user.org_id);
    console.debug("User Role:", user.role);
  }, []);

  const handleLogout = useCallback(() => {
    logoutUser();
    setAuthenticated(false);
    setLoggedInUser(null);
    console.debug("User logged out");
  }, []);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <LoginPage
              appMeta={appMeta}
              onLogin={handleLogin}
              setCurrentPage={setCurrentPage}
            />
          )
        }
      />

      <Route path="*" element={<NotFoundPage currentPage={currentPage} />} />

      {/* Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <AppShell
              appMeta={appMeta}
              navigation={navigation}
              currentUser={loggedInUser}
              onLogout={handleLogout}
              toasts={toasts}
              onDismissToast={dismissToast}
              setCurrentPage={setCurrentPage}
            />
          </ProtectedRoute>
        }
      >
        {/* Default routes */}
        <Route index element={<Navigate to="/dashboard" replace />} />

        <Route
          path="dashboard"
          element={
            <DashboardPage
              setCurrentPage={setCurrentPage}
            />
          }
        />

        <Route
          path="business"
        >
          <Route
            path="details"
            element={
              <BusinessDetailsPage
                notify={notify}
                setCurrentPage={setCurrentPage}
              />
            }
          />
          <Route
            path="intents"
            element={
              <IntentsPage
                data={intentsState}
                onChange={setIntentsState}
                notify={notify}
                setCurrentPage={setCurrentPage}
              />
            }
          />
          <Route
            path="pdfs"
            element={
              <PdfDocumentsPage
                notify={notify}
                setCurrentPage={setCurrentPage}
              />
            }
          />
        </Route>

        <Route
          path="agent-tone-control"
          element={
            <AgentToneControlPage
              notify={notify}
              setCurrentPage={setCurrentPage}
            />
          }
        />

        <Route
          path="settings"
          element={
            <SettingsPage
              data={settingsState}
              onChange={setSettingsState}
              notify={notify}
              setCurrentPage={setCurrentPage}
            />
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
